import { useState, useCallback, ChangeEvent, ClipboardEvent } from "react";
import {
  CON_MAX_ITEM_NAME_LENGTH,
  CON_MAX_MODEL_NAME_LENGTH,
} from "../utils/sys/Constants.tsx";
import { isNotEmpty } from "../utils/common/Validation.tsx";

// Types and Interfaces
export interface FormData {
  brand: string;
  mainCategory: string;
  category: string;
  subCategory: string;
  genericName: string;
  itemName: string;
  uom: string;
  packageType: string;
  unitsPerPackage: string;
  description: string;
  modelName: string;
}

export interface FormErrors {
  brand?: string;
  mainCategory?: string;
  category?: string;
  subCategory?: string;
  genericName?: string;
  itemName?: string;
  uom?: string;
  packageType?: string;
  unitsPerPackage?: string;
  modelName?: string;
  description?: string;
}

export interface EditItemData {
  itemname?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  measuringunitcode?: string | number;
  packagecode?: string | number;
  packageunits?: number | string;
  descrip?: string;
  [key: string]: any;
}

export interface ValidationResult {
  valid: boolean;
  errors: FormErrors;
}

export interface UseItemFormReturn {
  formData: FormData;
  errors: FormErrors;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  handleInputChange: (field: string, value: string) => void;
  handleTextInputChange: (field: string, e: ChangeEvent<HTMLInputElement>) => void;
  handleTextBlur: (field: string, e: ChangeEvent<HTMLInputElement>) => void;
  handleTextPaste: (field: string, e: ClipboardEvent<HTMLInputElement>) => void;
  handleDescriptionChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleUnitsPerPackageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  validateForm: () => ValidationResult;
  populateFormWithEditData: (itemData: EditItemData) => void;
  resetForm: () => void;
}

const INITIAL_FORM_DATA: FormData = {
  brand: "",
  mainCategory: "",
  category: "",
  subCategory: "",
  genericName: "",
  itemName: "",
  uom: "",
  packageType: "",
  unitsPerPackage: "",
  description: "",
  modelName: "",
};

export const useItemForm = (isEditMode: boolean = false): UseItemFormReturn => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});

  const handleInputChange = useCallback(
    (field: string, value: string): void => {
      setFormData((prev: FormData) => ({ ...prev, [field]: value }));

      // Clear validation error for this field
      if (errors[field as keyof FormErrors]) {
        setErrors((prev: FormErrors) => ({ ...prev, [field]: "" }));
      }
    },
    [errors]
  );

  const handleTextInputChange = useCallback(
    (field: string, e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;

      // Always remove leading spaces for itemName and modelName
      let processedValue = value;
      if ((field === "itemName" || field === "modelName") && value.startsWith(" ")) {
        processedValue = value.trimStart();
      }

      // Check character limit
      const maxLength =
        field === "itemName"
          ? CON_MAX_ITEM_NAME_LENGTH
          : CON_MAX_MODEL_NAME_LENGTH;

      if (processedValue.length <= maxLength) {
        // Within limit - update value and clear any limit error
        handleInputChange(field, processedValue);
        setErrors((prev: FormErrors) => ({ ...prev, [field]: "" }));
      } else {
        // Exceeds limit - show error but don't update value
        setErrors((prev: FormErrors) => ({
          ...prev,
          [field]: `Maximum ${maxLength} characters allowed.`,
        }));
      }
    },
    [handleInputChange, setErrors]
  );

  // Handle blur to trim trailing spaces for itemName and modelName
  const handleTextBlur = useCallback(
    (field: string, e: ChangeEvent<HTMLInputElement>): void => {
      if (field === "itemName" || field === "modelName") {
        const trimmedValue = e.target.value.trim();
        if (trimmedValue !== e.target.value) {
          handleInputChange(field, trimmedValue);
        }
      }
    },
    [handleInputChange]
  );

  // Handle paste to trim both leading and trailing spaces
  const handleTextPaste = useCallback(
    (field: string, e: ClipboardEvent<HTMLInputElement>): void => {
      if (field === "itemName" || field === "modelName") {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");
        const trimmedText = pastedText.trim();
        
        // Check character limit
        const maxLength =
          field === "itemName"
            ? CON_MAX_ITEM_NAME_LENGTH
            : CON_MAX_MODEL_NAME_LENGTH;

        if (trimmedText.length <= maxLength) {
          handleInputChange(field, trimmedText);
          setErrors((prev: FormErrors) => ({ ...prev, [field]: "" }));
        } else {
          setErrors((prev: FormErrors) => ({
            ...prev,
            [field]: `Maximum ${maxLength} characters allowed.`,
          }));
        }
      }
    },
    [handleInputChange, setErrors]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      handleInputChange("description", value);
    },
    [handleInputChange]
  );

  const handleUnitsPerPackageChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      // Allow only numbers
      if (/^\d*$/.test(value)) {
        handleInputChange("unitsPerPackage", value);
      }
    },
    [handleInputChange]
  );

  const validateForm = useCallback((): ValidationResult => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.brand) newErrors.brand = "Brand is required";
    if (!formData.mainCategory)
      newErrors.mainCategory = "Main Category is required";
    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.subCategory)
      newErrors.subCategory = "Subcategory is required";
    if (!formData.genericName)
      newErrors.genericName = "Generic Name is required";
    if (!formData.uom) newErrors.uom = "Unit of Measure is required";
    if (!formData.packageType)
      newErrors.packageType = "Package Type is required";
    if (!formData.unitsPerPackage)
      newErrors.unitsPerPackage = "Units Per Package is required";

    // Item Name validation
    if (!isNotEmpty(formData.itemName)) {
      newErrors.itemName = "Item Name is required";
    } else if (formData.itemName.length > CON_MAX_ITEM_NAME_LENGTH) {
      newErrors.itemName = `Item Name cannot exceed ${CON_MAX_ITEM_NAME_LENGTH} characters`;
    }

    // Units Per Package validation
    if (
      formData.unitsPerPackage &&
      (isNaN(parseInt(formData.unitsPerPackage)) ||
        parseInt(formData.unitsPerPackage) <= 0)
    ) {
      newErrors.unitsPerPackage =
        "Units Per Package must be a positive number";
    }

    // Model Name validation (optional but with character limit)
    if (formData.modelName.length > CON_MAX_MODEL_NAME_LENGTH) {
      newErrors.modelName = `Model Name cannot exceed ${CON_MAX_MODEL_NAME_LENGTH} characters`;
    }

    setErrors(newErrors);
    const valid = Object.keys(newErrors).length === 0;
    return { valid, errors: newErrors };
  }, [formData]);

  const populateFormWithEditData = useCallback((itemData: EditItemData): void => {
    const trimmedItemName = (itemData.itemname || "").trim();
    const trimmedModelName = (itemData.modelname || "").trim();

    setFormData({
      brand: itemData.brandcode?.toString() || "",
      mainCategory: itemData.maincatcode?.toString() || "",
      category: itemData.catcode?.toString() || "",
      subCategory: itemData.subcatcode?.toString() || "",
      genericName: itemData.genericcode?.toString() || "",
      itemName: trimmedItemName,
      uom: itemData.measuringunitcode?.toString() || "",
      packageType: itemData.packagecode?.toString() || "",
      unitsPerPackage: itemData.packageunits?.toString() || "",
      description: itemData.descrip || "",
      modelName: trimmedModelName,
    });
  }, []);

  const resetForm = useCallback((): void => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    setFormData,
    setErrors,
    handleInputChange,
    handleTextInputChange,
    handleTextBlur,
    handleTextPaste,
    handleDescriptionChange,
    handleUnitsPerPackageChange,
    validateForm,
    populateFormWithEditData,
    resetForm,
  };
};